import type { FC, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../ui/Spinner';

import { type CheckAccess, useAccessPoll } from '../useAccessPoll';

interface AwaitingAccessStepProps {
  onAccessGranted: () => void;
  checkAccess: CheckAccess;
  /** Replaces the default spinner body; the flag says the poll gave up. */
  children?: (isTimedOut: boolean) => ReactNode;
}

// Pure bridge: polls for access and signals the wrapper through onAccessGranted,
// which owns what happens next (the join handoff, or unlocking the channel in place).
export const AwaitingAccessStep: FC<AwaitingAccessStepProps> = ({ onAccessGranted, checkAccess, children }) => {
  const { t } = useTranslation();
  const status = useAccessPoll({ enabled: true, checkAccess });
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    if (status === 'granted' && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      onAccessGranted();
    }
  }, [status, onAccessGranted]);

  // Only 'timeout' gets its own copy
  const isTimedOut = status === 'timeout';

  if (children) return children(isTimedOut);

  return (
    <div role="status" className="mt-4 flex flex-col items-center gap-3 py-2 text-center">
      {!isTimedOut && <Spinner className="size-6" />}
      <p className="text-sm leading-5 text-black/50">
        {t(isTimedOut ? 'community.upgrade_modal.payment_received' : 'community.upgrade_modal.preparing_access')}
      </p>
    </div>
  );
};
